/**
 * Finding a place on the geographic board — by coordinates, or by name.
 *
 * TWO ways in, and the difference between them matters:
 *
 *   COORDINATES are parsed HERE, in the browser, with no network at all. A site with no route
 *   out has no basemap either, but it still has a map with pins on it, and an operator who
 *   knows where a camera is can still go there. That is why the parser is generous about
 *   format and why nothing about it depends on the geocoder being reachable.
 *
 *   NAMES need a geocoder, which is a network service like the tile source and has the same
 *   consequences: it may be absent, slow, or replaced by one the site runs itself. So it is
 *   configured the same way (`geo_map_config.geocoder_url`, defaulting to public Nominatim)
 *   and its failure is reported as a state, not swallowed.
 *
 * ON NOMINATIM'S TERMS OF USE
 * ───────────────────────────
 * The public endpoint asks for at most one request a second and no search-as-you-type. This
 * module therefore only ever runs on an explicit submit — never on a keystroke — and asks for
 * a handful of results. A deployment that searches heavily is expected to point
 * `geocoder_url` at its own instance, which is also the answer for a site with no internet.
 */

/** A place the operator can jump to. */
export interface GeoSearchResult {
  /** What to show in the list — "Bandra Kurla Complex, Mumbai, Maharashtra, India". */
  label: string;
  lat: number;
  lng: number;
  /** [south, west, north, east] when the geocoder gives one: a city should frame, not zoom. */
  bbox?: [number, number, number, number];
}

/** A coordinate the operator typed, and what had to be assumed to read it. */
export interface ParsedCoordinates {
  lat: number;
  lng: number;
  /**
   * True when the pair was given as lng,lat and read in that order. Reported rather than
   * silently corrected: "20, 78" is a valid pair either way round, and an operator who meant
   * the other one needs to see that something was decided for them.
   */
  swapped: boolean;
  /** Carried out of a pasted map URL that named one. */
  zoom?: number;
}

/**
 * The public OpenStreetMap geocoder. `{q}` is the URL-encoded query.
 *
 * Mirrors DEFAULT_TILE_URL on the node: a sensible public default that a site can replace,
 * not a promise that the internet is there.
 */
export const DEFAULT_GEOCODER_URL =
  'https://nominatim.openstreetmap.org/search?format=jsonv2&limit=6&q={q}';

/**
 * One coordinate component: an optional hemisphere either side, degrees, and optionally
 * minutes and seconds.
 *
 * Written as one expression because the formats are not really different formats — they are
 * the same number with parts omitted. `19.076`, `19.076° N`, `N19.076`, `19°4'33.6"N` and
 * `-19.076` are all the same place, and an operator pastes whichever their other tool gave
 * them rather than the one this field prefers.
 */
const COMPONENT = String.raw`(?:([NSEW])\s*)?([+-]?\d+(?:\.\d+)?)\s*(?:°|º|deg)?\s*(?:(\d+(?:\.\d+)?)\s*['′’]\s*(?:(\d+(?:\.\d+)?)\s*(?:"|″|”)?\s*)?)?(?:\s*([NSEW]))?`;

/** Two components, separated by a comma, a slash or just space. */
const PAIR = new RegExp(`^\\s*${COMPONENT}\\s*[,;/]?\\s+?\\s*${COMPONENT}\\s*$`, 'i');
const PAIR_COMMA = new RegExp(`^\\s*${COMPONENT}\\s*[,;/]\\s*${COMPONENT}\\s*$`, 'i');

/**
 * What pasting from a map application gives you. Three shapes, because the two applications
 * an operator is most likely to have open write them differently:
 *   google : /maps/@19.07,72.87,14z   and   ?q=19.07,72.87
 *   osm    : #map=15/19.07/72.87      and   ?mlat=19.07&mlon=72.87
 */
const URL_AT = /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)(?:,(\d+(?:\.\d+)?)z)?/;
const URL_Q = /[?&](?:q|query|ll|center)=(-?\d+(?:\.\d+)?)(?:,|%2C)(-?\d+(?:\.\d+)?)/i;
/** OSM's permalink puts the zoom FIRST: #map=<zoom>/<lat>/<lng>. */
const URL_HASH = /#map=(\d+(?:\.\d+)?)\/(-?\d+(?:\.\d+)?)\/(-?\d+(?:\.\d+)?)/i;
/** …and its marker links carry the pair as two separate parameters. */
const URL_MLAT = /[?&](?:mlat|lat)=(-?\d+(?:\.\d+)?)/i;
const URL_MLON = /[?&](?:mlon|mlng|lon|lng)=(-?\d+(?:\.\d+)?)/i;

function componentValue(
  lead: string | undefined,
  deg: string,
  min: string | undefined,
  sec: string | undefined,
  trail: string | undefined,
): { value: number; axis: 'lat' | 'lng' | null } | null {
  const hemi = (lead || trail || '').toUpperCase();
  // A hemisphere on both ends contradicts itself; so does a signed number that also says S.
  if (lead && trail) return null;
  const degrees = parseFloat(deg);
  if (!Number.isFinite(degrees)) return null;
  if ((min !== undefined || sec !== undefined) && degrees < 0) return null;
  const magnitude = Math.abs(degrees)
    + (min !== undefined ? parseFloat(min) / 60 : 0)
    + (sec !== undefined ? parseFloat(sec) / 3600 : 0);
  const negative = degrees < 0 || hemi === 'S' || hemi === 'W';
  return {
    value: negative ? -magnitude : magnitude,
    axis: hemi === 'N' || hemi === 'S' ? 'lat' : hemi === 'E' || hemi === 'W' ? 'lng' : null,
  };
}

const inLat = (v: number) => v >= -90 && v <= 90;
const inLng = (v: number) => v >= -180 && v <= 180;

/**
 * Read a coordinate pair out of whatever the operator typed, or null if it is not one.
 *
 * Null is the ordinary answer, not an error: this runs on every keystroke to decide whether
 * the box is holding coordinates or a place name, and "Bandra" is simply not a coordinate.
 */
export function parseCoordinates(input: string): ParsedCoordinates | null {
  const text = (input || '').trim();
  if (!text) return null;

  // A pasted map URL first — it contains a comma-separated pair that the general parser would
  // also find, but only the URL carries the zoom the operator was looking at.
  if (/https?:\/\//i.test(text) || text.includes('/maps') || text.includes('#map=')) {
    let lat: number | null = null;
    let lng: number | null = null;
    let zoom: number | undefined;

    const hash = URL_HASH.exec(text);
    const pair = URL_AT.exec(text) || URL_Q.exec(text);
    if (hash) {
      zoom = Math.round(parseFloat(hash[1]));
      lat = parseFloat(hash[2]);
      lng = parseFloat(hash[3]);
    } else if (pair) {
      lat = parseFloat(pair[1]);
      lng = parseFloat(pair[2]);
      if (pair[3]) zoom = Math.round(parseFloat(pair[3]));
    } else {
      const la = URL_MLAT.exec(text);
      const lo = URL_MLON.exec(text);
      if (la && lo) {
        lat = parseFloat(la[1]);
        lng = parseFloat(lo[1]);
      }
    }

    if (lat === null || lng === null || !inLat(lat) || !inLng(lng)) return null;
    return { lat, lng, swapped: false, ...(zoom !== undefined ? { zoom } : {}) };
  }

  const m = PAIR_COMMA.exec(text) || PAIR.exec(text);
  if (!m) return null;

  const a = componentValue(m[1], m[2], m[3], m[4], m[5]);
  const b = componentValue(m[6], m[7], m[8], m[9], m[10]);
  if (!a || !b) return null;

  // Hemispheres, when given, say which is which — so "72.8777E, 19.076N" is not ambiguous
  // and is not a swap. Without them the order is the conventional lat, lng.
  let lat: number;
  let lng: number;
  let swapped = false;
  if (a.axis === 'lng' || b.axis === 'lat') {
    if (a.axis === b.axis) return null; // "19N 72N" names no place
    lat = b.value;
    lng = a.value;
  } else {
    lat = a.value;
    lng = b.value;
  }

  // Out of range one way round and fine the other means the pair is lng,lat — common, because
  // that is the order GeoJSON and several GIS exports use.
  if (!inLat(lat) && inLat(lng) && inLng(lat) && !a.axis && !b.axis) {
    [lat, lng] = [lng, lat];
    swapped = true;
  }
  if (!inLat(lat) || !inLng(lng)) return null;
  return { lat, lng, swapped };
}

/** Round-trip safe display form — what the "go to" row shows back to the operator. */
export function formatCoordinates(lat: number, lng: number): string {
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

function buildUrl(template: string, query: string): string {
  const q = encodeURIComponent(query);
  if (template.includes('{q}')) return template.replace(/\{q\}/g, q);
  // A URL with no placeholder still works: a geocoder takes its query somewhere, and `q` is
  // what Nominatim and Photon both call it.
  return `${template}${template.includes('?') ? '&' : '?'}q=${q}`;
}

function toNumber(v: unknown): number | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? parseFloat(v) : NaN;
  return Number.isFinite(n) ? n : null;
}

/**
 * Read whatever the configured geocoder answered.
 *
 * Nominatim's array and a GeoJSON FeatureCollection (what Photon returns) are both accepted,
 * because "point it at your own geocoder" is only a real option if the common self-hosted one
 * works without a code change.
 */
function parseResults(body: any): GeoSearchResult[] {
  const rows: any[] = Array.isArray(body)
    ? body
    : Array.isArray(body?.features)
      ? body.features
      : [];

  const out: GeoSearchResult[] = [];
  for (const row of rows) {
    // GeoJSON keeps the position in geometry.coordinates as [lng, lat].
    const coords = row?.geometry?.coordinates;
    const lat = toNumber(row?.lat ?? row?.latitude ?? (Array.isArray(coords) ? coords[1] : undefined));
    const lng = toNumber(row?.lon ?? row?.lng ?? row?.longitude ?? (Array.isArray(coords) ? coords[0] : undefined));
    if (lat === null || lng === null || !inLat(lat) || !inLng(lng)) continue;

    const props = row?.properties || {};
    const label = String(
      row?.display_name || props.display_name
      || [props.name, props.city, props.state, props.country].filter(Boolean).join(', ')
      || row?.name || formatCoordinates(lat, lng),
    );

    // Nominatim: [south, north, west, east] as strings. Ours: [south, west, north, east].
    const bb = row?.boundingbox;
    const bbox = Array.isArray(bb) && bb.length === 4
      ? ([toNumber(bb[0]), toNumber(bb[2]), toNumber(bb[1]), toNumber(bb[3])].every((n) => n !== null)
        ? ([toNumber(bb[0]), toNumber(bb[2]), toNumber(bb[1]), toNumber(bb[3])] as [number, number, number, number])
        : undefined)
      : undefined;

    out.push({ label, lat, lng, ...(bbox ? { bbox } : {}) });
  }
  return out;
}

/** Thrown so the box can say WHY nothing came back — unreachable is not "no results". */
export class GeocoderUnreachable extends Error {
  constructor(message = 'Place search is unavailable from this browser') {
    super(message);
    this.name = 'GeocoderUnreachable';
  }
}

/**
 * Look a place name up. Explicit submit only — see the note on Nominatim's terms above.
 */
export async function geocode(
  query: string,
  opts: { url?: string; signal?: AbortSignal } = {},
): Promise<GeoSearchResult[]> {
  const q = (query || '').trim();
  if (!q) return [];
  const url = buildUrl(opts.url || DEFAULT_GEOCODER_URL, q);

  let res: Response;
  try {
    res = await fetch(url, {
      signal: opts.signal,
      headers: { Accept: 'application/json' },
      // No cookies to a third-party geocoder; the query is all it gets.
      credentials: 'omit',
      referrerPolicy: 'strict-origin-when-cross-origin',
    });
  } catch (e: any) {
    // An abort is the caller changing their mind, not a broken geocoder.
    if (e?.name === 'AbortError') throw e;
    throw new GeocoderUnreachable();
  }
  if (!res.ok) {
    throw new GeocoderUnreachable(
      res.status === 429
        ? 'Place search is rate-limited — try again in a moment'
        : `Place search failed (${res.status})`,
    );
  }
  try {
    return parseResults(await res.json());
  } catch {
    throw new GeocoderUnreachable('Place search returned something unreadable');
  }
}
